import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import AlertToast from '../../components/common/AlertToast';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PolicyTab from '../../components/common/PolicyTab';
import WebsiteBlogAccess from './WebsiteBlogAccess';

const ACTIONS = ['view', 'create', 'edit', 'delete', 'approve', 'export'];

const ACTION_COLORS = {
  view: 'info', create: 'success', edit: 'warning',
  delete: 'danger', approve: 'primary', export: 'secondary',
};

const ROLE_BADGE = {
  super_admin: 'danger', admin: 'warning', manager: 'info',
  team_lead: 'primary', faculty: 'success', student: 'secondary',
};

const SYSTEM_MODULES = [
  { key: 'dashboard',        name: 'Dashboard' },
  { key: 'courses',          name: 'Courses' },
  { key: 'batches',          name: 'Batches' },
  { key: 'enrollments',      name: 'Enrollments' },
  { key: 'enquiries',        name: 'Enquiries' },
  { key: 'attendance',       name: 'Attendance' },
  { key: 'course-content',   name: 'Course Content' },
  { key: 'course-records',   name: 'Course Records' },
  { key: 'assessments',      name: 'Assessments' },
  { key: 'tasks',            name: 'Tasks' },
  { key: 'resumes',          name: 'Resumes' },
  { key: 'daily-reports',    name: 'Daily Work Records' },
  { key: 'leave',            name: 'Leave Management' },
  { key: 'work-attendance',  name: 'Work Attendance' },
  { key: 'hrm',              name: 'HRM' },
  { key: 'chat',             name: 'Chat' },
  { key: 'timetable',        name: 'Timetable' },
  { key: 'feedback',         name: 'Feedback' },
  { key: 'users',            name: 'Users' },
  { key: 'roles',            name: 'Roles & Permissions' },
  { key: 'settings',         name: 'Settings' },
];

function emptyMatrix() {
  const m = {};
  SYSTEM_MODULES.forEach(mod => {
    m[mod.key] = {};
    ACTIONS.forEach(a => { m[mod.key][a] = false; });
  });
  return m;
}

// ─── Role Form Modal ──────────────────────────────────────────────────────────
function RoleFormModal({ show, mode, initial, onClose, onSaved }) {
  const [form, setForm]   = useState({ name: '', description: '', level: 10 });
  const [saving, setSaving] = useState(false);
  const [err, setErr]     = useState('');

  useEffect(() => {
    if (show) {
      setForm(initial
        ? { name: initial.name, description: initial.description || '', level: initial.level || 10 }
        : { name: '', description: '', level: 10 }
      );
      setErr('');
    }
  }, [show, initial]);

  if (!show) return null;

  const handle = async (e) => {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      let r;
      if (mode === 'edit') {
        r = await api.put(`/roles/${initial._id}`, form);
      } else {
        r = await api.post('/roles', form);
      }
      onSaved(r.data?.data || r.data);
    } catch (e) { setErr(e.response?.data?.message || e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1050 }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title"><i className="bi bi-shield me-2"></i>{mode === 'edit' ? 'Edit Role' : 'Create Role'}</h5>
            <button className="btn-close" onClick={onClose}></button>
          </div>
          <form onSubmit={handle}>
            <div className="modal-body">
              {err && <div className="alert alert-danger py-2">{err}</div>}
              <div className="mb-3">
                <label className="form-label">Name <span className="text-danger">*</span></label>
                <input className="form-control" value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required />
                {mode !== 'edit' && <small className="text-muted">Key will be auto-generated from name</small>}
              </div>
              <div className="mb-3">
                <label className="form-label">Description</label>
                <textarea className="form-control" rows={2} value={form.description}
                  onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
              </div>
              <div className="mb-3">
                <label className="form-label">Level <small className="text-muted">(0–99, higher = more privilege)</small></label>
                <input type="number" className="form-control" value={form.level} min={0} max={99}
                  onChange={e => setForm(p => ({ ...p, level: Number(e.target.value) }))} />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving...</> : <><i className="bi bi-check-lg me-1"></i>Save</>}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── Permission Matrix Modal ──────────────────────────────────────────────────
function MatrixModal({ show, role, activeModules, onClose, onSaved }) {
  const [matrix,    setMatrix]    = useState(emptyMatrix());
  const [menuAccess, setMenuAccess] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [err,       setErr]       = useState('');

  useEffect(() => {
    if (show && role) {
      setLoading(true);
      api.get(`/roles/${role._id}`)
        .then(r => {
          const data = r.data?.data || r.data;
          const base = emptyMatrix();
          const existing = data?.permissionMatrix || {};
          SYSTEM_MODULES.forEach(mod => {
            ACTIONS.forEach(a => {
              if (existing[mod.key]) base[mod.key][a] = !!existing[mod.key][a];
            });
          });
          setMatrix(base);
          setMenuAccess(data?.menuAccess || []);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [show, role]);

  if (!show || !role) return null;

  // Menu visibility (sidebar) is now driven by permissionMatrix.view —
  // toggling `view` adds/removes the module from the sidebar, no separate
  // Menu column to remember. We keep `menuAccess` in state purely as a
  // mirror of the modules currently set to view:true so the existing
  // server payload + UI consumers stay backwards-compatible.
  const setView = (modKey, value) => {
    setMatrix(m => ({ ...m, [modKey]: { ...m[modKey], view: value } }));
    setMenuAccess(prev => {
      const has = prev.includes(modKey);
      if (value && !has) return [...prev, modKey];
      if (!value && has) return prev.filter(k => k !== modKey);
      return prev;
    });
  };

  const toggle = (modKey, action) => {
    if (action === 'view') return setView(modKey, !matrix[modKey]?.view);
    setMatrix(m => ({ ...m, [modKey]: { ...m[modKey], [action]: !m[modKey][action] } }));
  };

  const toggleModule = (modKey) => {
    const allOn = ACTIONS.every(a => matrix[modKey]?.[a]);
    const next  = Object.fromEntries(ACTIONS.map(a => [a, !allOn]));
    setMatrix(m => ({ ...m, [modKey]: next }));
    // Module-level toggle — sync menuAccess with the new view state.
    setMenuAccess(prev => {
      const has = prev.includes(modKey);
      if (next.view && !has)  return [...prev, modKey];
      if (!next.view && has)  return prev.filter(k => k !== modKey);
      return prev;
    });
  };

  const toggleAction = (action) => {
    const allOn = SYSTEM_MODULES.every(mod => matrix[mod.key]?.[action]);
    setMatrix(m => {
      const next = { ...m };
      SYSTEM_MODULES.forEach(mod => { next[mod.key] = { ...next[mod.key], [action]: !allOn }; });
      return next;
    });
    if (action === 'view') {
      // Column-level view toggle — sync menuAccess for every module.
      setMenuAccess(allOn ? [] : SYSTEM_MODULES.map(m => m.key));
    }
  };

  const handleSave = async () => {
    setSaving(true); setErr('');
    try {
      // Backend now derives canonical menuAccess from permissionMatrix.view —
      // we send menuAccess only as a hint; the server always rebuilds it.
      const r = await api.put(`/roles/${role._id}/permissions`, { permissionMatrix: matrix, menuAccess });
      onSaved(r.data?.data || r.data);
    } catch (e) { setErr(e.response?.data?.message || e.message); }
    finally { setSaving(false); }
  };

  const allModules = activeModules?.length > 0
    ? [...new Map([...SYSTEM_MODULES, ...activeModules.map(m => ({ key: m.key, name: m.name }))].map(m => [m.key, m])).values()]
    : SYSTEM_MODULES;

  return (
    <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1050 }}>
      <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-shield-check me-2"></i>Permission Matrix — {role.name}
              {role.isSystem && <span className="badge bg-warning ms-2" style={{ fontSize: '0.7rem' }}>System</span>}
            </h5>
            <button className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body p-0">
            {err && <div className="alert alert-danger m-2 py-2">{err}</div>}
            {loading ? <div className="text-center py-5"><div className="spinner-border text-primary"></div></div> : (
              <div className="table-responsive" style={{ maxHeight: '65vh' }}>
                <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.8rem' }}>
                  <thead className="table-dark sticky-top">
                    <tr>
                      <th style={{ minWidth: 160 }}>Module</th>
                      <th className="text-center" style={{ minWidth: 70 }} title="Sidebar visibility — derived from the View column">
                        Menu<br/><span style={{ fontSize: '0.6rem', opacity: 0.7, fontWeight: 400 }}>(= View)</span>
                      </th>
                      {ACTIONS.map(a => (
                        <th key={a} className="text-center" style={{ minWidth: 65, cursor: 'pointer' }}
                          onClick={() => toggleAction(a)}
                          title={`Toggle all ${a}`}>
                          <span className={`badge bg-${ACTION_COLORS[a]} text-capitalize`}>{a}</span>
                          <div style={{ fontSize: '0.6rem', opacity: 0.7 }}>▲ all</div>
                        </th>
                      ))}
                      <th className="text-center" style={{ minWidth: 50 }}>All</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allModules.map(mod => {
                      const row   = matrix[mod.key] || {};
                      const allOn = ACTIONS.every(a => row[a]);
                      const anyOn = ACTIONS.some(a => row[a]);
                      const inMenu = !!row.view; // sidebar = view permission
                      return (
                        <tr key={mod.key} className={anyOn ? '' : 'table-light'}>
                          <td className="fw-semibold">{mod.name}</td>
                          <td className="text-center" style={{ background: inMenu ? '#ecfdf5' : '#fef2f2' }}>
                            <input type="checkbox" className="form-check-input"
                              checked={inMenu}
                              onChange={() => setView(mod.key, !inMenu)}
                              title="Show this module in the sidebar (linked to View)" />
                          </td>
                          {ACTIONS.map(a => (
                            <td key={a} className="text-center">
                              <input type="checkbox" className="form-check-input"
                                checked={!!row[a]} onChange={() => toggle(mod.key, a)} />
                            </td>
                          ))}
                          <td className="text-center">
                            <input type="checkbox" className="form-check-input"
                              checked={allOn}
                              ref={el => { if (el) el.indeterminate = !allOn && anyOn; }}
                              onChange={() => toggleModule(mod.key)} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving || loading}>
              {saving ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving...</> : <><i className="bi bi-check-lg me-1"></i>Save Permissions</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Audit Log Tab ────────────────────────────────────────────────────────────
function AuditLogTab() {
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/audit-logs', { params: { limit: 100 } });
      setLogs(r.data?.data || []);
    } catch (_) {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const ACTION_COLOR = { CREATE: 'success', UPDATE: 'warning', DELETE: 'danger', LOGIN: 'info', LOGOUT: 'secondary' };

  return (
    <div>
      <div className="d-flex justify-content-end mb-3">
        <button className="btn btn-sm btn-outline-secondary" onClick={fetch} disabled={loading}>
          <i className="bi bi-arrow-clockwise me-1"></i>Refresh
        </button>
      </div>
      {loading ? <LoadingSpinner /> : (
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>Time</th>
                <th>Action</th>
                <th>Module</th>
                <th>Description</th>
                <th>Performed By</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr><td colSpan={5} className="text-center text-muted py-4">No audit logs recorded yet.</td></tr>
              ) : logs.map(l => (
                <tr key={l._id}>
                  <td><small className="text-muted">{new Date(l.createdAt).toLocaleString('en-IN')}</small></td>
                  <td><span className={`badge bg-${ACTION_COLOR[l.action] || 'secondary'}`}>{l.action}</span></td>
                  <td><small>{l.module || l.resourceType || '—'}</small></td>
                  <td><small>{l.description || '—'}</small></td>
                  <td>
                    <small>
                      {l.userId?.firstName ? `${l.userId.firstName} ${l.userId.lastName}` : (l.userEmail || '—')}
                    </small>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Users Permissions Tab ────────────────────────────────────────────────────
function UserPermissionsTab({ roles }) {
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(null);
  const [toast,   setToast]   = useState(null);
  const [search,  setSearch]  = useState('');

  useEffect(() => {
    api.get('/users', { params: { limit: 500 } }).then(r => setUsers(r.data?.records || r.data?.data || r.data?.users || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleRoleChange = async (userId, newRoleKey) => {
    setSaving(userId);
    try {
      await api.put(`/users/${userId}`, { roleKey: newRoleKey });
      setUsers(prev => prev.map(u => u._id === userId ? { ...u, roleKey: newRoleKey } : u));
      setToast({ type: 'success', message: 'Role updated.' });
    } catch (e) {
      setToast({ type: 'danger', message: e.response?.data?.message || e.message });
    } finally { setSaving(null); }
  };

  const filtered = users.filter(u =>
    !search || `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      {toast && <AlertToast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
      <div className="alert alert-info py-2 mb-3">
        <i className="bi bi-info-circle me-2"></i>
        Assign roles to users. Role determines module access and permission matrix.
      </div>
      <div className="mb-3">
        <input className="form-control form-control-sm" style={{ maxWidth: 280 }}
          placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>
      {loading ? <LoadingSpinner /> : (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Current Role</th>
                <th>Change Role</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={4} className="text-center text-muted py-4">No users found.</td></tr>
              ) : filtered.map(u => {
                const badge = ROLE_BADGE[u.roleKey] || 'secondary';
                return (
                  <tr key={u._id}>
                    <td>
                      <div className="fw-semibold">{u.firstName} {u.lastName}</div>
                    </td>
                    <td><small className="text-muted">{u.email}</small></td>
                    <td><span className={`badge bg-${badge}`}>{u.roleKey || '—'}</span></td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <select
                          className="form-select form-select-sm"
                          style={{ maxWidth: 180 }}
                          value={u.roleKey || ''}
                          onChange={e => handleRoleChange(u._id, e.target.value)}
                          disabled={saving === u._id}
                        >
                          <option value="">— Select role —</option>
                          {roles.map(r => <option key={r._id} value={r.key}>{r.name}</option>)}
                        </select>
                        {saving === u._id && <span className="spinner-border spinner-border-sm text-primary"></span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Main Roles Page ──────────────────────────────────────────────────────────
export default function Roles() {
  const { activeModules } = useAuth();

  const [tab,     setTab]     = useState('roles');
  const [roles,   setRoles]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast,   setToast]   = useState(null);

  const [formModal,   setFormModal]   = useState({ show: false, mode: 'create', role: null });
  const [matrixModal, setMatrixModal] = useState({ show: false, role: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/roles', { params: { includeInactive: 'true' } });
      setRoles(r.data?.data || r.data?.records || []);
    } catch (e) { setToast({ type: 'danger', message: e.message }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (role) => {
    if (role.isSystem) { setToast({ type: 'warning', message: 'System roles cannot be deleted.' }); return; }
    if (!window.confirm(`Delete role "${role.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/roles/${role._id}`);
      setRoles(prev => prev.filter(r => r._id !== role._id));
      setToast({ type: 'success', message: 'Role deleted.' });
    } catch (e) { setToast({ type: 'danger', message: e.response?.data?.message || e.message }); }
  };

  const handleClone = async (role) => {
    try {
      const r = await api.post(`/roles/${role._id}/clone`, {});
      setRoles(prev => [...prev, r.data?.data || r.data]);
      setToast({ type: 'success', message: 'Role cloned.' });
    } catch (e) { setToast({ type: 'danger', message: e.response?.data?.message || e.message }); }
  };

  const handleToggleActive = async (role) => {
    if (role.isSystem) { setToast({ type: 'warning', message: 'System roles cannot be deactivated.' }); return; }
    try {
      const r = await api.put(`/roles/${role._id}`, { isActive: !role.isActive });
      const updated = r.data?.data || r.data;
      setRoles(prev => prev.map(x => x._id === updated._id ? updated : x));
    } catch (e) { setToast({ type: 'danger', message: e.response?.data?.message || e.message }); }
  };

  const handleSaved = (saved) => {
    if (!saved) { load(); return; }
    setRoles(prev => {
      const i = prev.findIndex(r => r._id === saved._id);
      return i >= 0 ? prev.map(r => r._id === saved._id ? saved : r) : [...prev, saved];
    });
    setFormModal(d => ({ ...d, show: false }));
    setMatrixModal({ show: false, role: null });
    setToast({ type: 'success', message: 'Saved.' });
  };

  const permCount = (role) =>
    Object.values(role.permissionMatrix || {}).reduce((s, m) => s + Object.values(m).filter(Boolean).length, 0);

  return (
    <div>
      {toast && <AlertToast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      <PageHeader
        title="Roles & Permissions"
        icon="bi-shield-lock"
        subtitle="Manage roles, permission matrices and user assignments"
        actions={tab === 'roles' && (
          <button className="btn btn-primary btn-sm"
            onClick={() => setFormModal({ show: true, mode: 'create', role: null })}>
            <i className="bi bi-plus-lg me-1"></i>New Role
          </button>
        )}
      />

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        {[
          { key: 'roles',    label: 'Roles',              icon: 'bi-shield' },
          { key: 'blog-access', label: 'Website Blog Access', icon: 'bi-journal-richtext' },
          { key: 'users',    label: 'User Assignments',   icon: 'bi-person-badge' },
          { key: 'audit',    label: 'Audit Log',          icon: 'bi-clock-history' },
          { key: 'policy',   label: 'Policy / Manual',    icon: 'bi-journal-text' },
        ].map(t => (
          <li className="nav-item" key={t.key}>
            <button className={`nav-link ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
              <i className={`bi ${t.icon} me-1`}></i>{t.label}
            </button>
          </li>
        ))}
      </ul>

      {/* ── Roles Tab ── */}
      {tab === 'roles' && (
        loading ? <LoadingSpinner /> : (
          <div className="row g-3">
            {roles.length === 0 && (
              <div className="col-12 text-center text-muted py-5">No roles found.</div>
            )}
            {roles.map(role => {
              const pc    = permCount(role);
              const badge = ROLE_BADGE[role.key] || 'secondary';
              return (
                <div className="col-sm-6 col-xl-4" key={role._id}>
                  <div className={`card h-100 shadow-sm ${!role.isActive ? 'opacity-50' : ''}`}>
                    <div className="card-body">
                      <div className="d-flex align-items-start mb-2">
                        <div className="flex-grow-1">
                          <span className={`badge bg-${badge} me-1`}>{role.name}</span>
                          {role.isSystem && <span className="badge bg-secondary" style={{ fontSize: '0.65rem' }}>System</span>}
                          {!role.isActive && <span className="badge bg-danger ms-1" style={{ fontSize: '0.65rem' }}>Inactive</span>}
                        </div>
                        <small className="text-muted">Lvl {role.level || 0}</small>
                      </div>
                      <div className="text-muted" style={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>{role.key}</div>
                      {role.description && <p className="small text-muted mt-1 mb-2">{role.description}</p>}
                      <div className="d-flex gap-1 flex-wrap mt-2">
                        <span className="badge bg-light text-dark border">{pc} permissions</span>
                        <span className="badge bg-light text-dark border">{(role.menuAccess || []).length} menu items</span>
                      </div>
                    </div>
                    <div className="card-footer d-flex gap-1 flex-wrap py-2">
                      <button className="btn btn-sm btn-outline-primary"
                        onClick={() => setMatrixModal({ show: true, role })}>
                        <i className="bi bi-shield-check me-1"></i>Matrix
                      </button>
                      <button className="btn btn-sm btn-outline-secondary"
                        onClick={() => setFormModal({ show: true, mode: 'edit', role })}>
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button className="btn btn-sm btn-outline-secondary"
                        onClick={() => handleClone(role)} title="Clone">
                        <i className="bi bi-copy"></i>
                      </button>
                      {!role.isSystem && (
                        <>
                          <button
                            className={`btn btn-sm ${role.isActive ? 'btn-outline-warning' : 'btn-outline-success'}`}
                            onClick={() => handleToggleActive(role)}
                            title={role.isActive ? 'Deactivate' : 'Activate'}>
                            <i className={`bi ${role.isActive ? 'bi-toggle-off' : 'bi-toggle-on'}`}></i>
                          </button>
                          <button className="btn btn-sm btn-outline-danger ms-auto"
                            onClick={() => handleDelete(role)} title="Delete">
                            <i className="bi bi-trash"></i>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ── User Assignments Tab ── */}
      {tab === 'blog-access' && <WebsiteBlogAccess />}
      {tab === 'users' && <UserPermissionsTab roles={roles} />}

      {/* ── Audit Log Tab ── */}
      {tab === 'audit' && <AuditLogTab />}

      {/* ── Policy / Manual Tab ── */}
      {tab === 'policy' && <PolicyTab moduleKey="roles-permissions" label="Roles & Permissions Policy" />}

      {/* Modals */}
      <RoleFormModal
        show={formModal.show}
        mode={formModal.mode}
        initial={formModal.role}
        onClose={() => setFormModal(d => ({ ...d, show: false }))}
        onSaved={handleSaved}
      />
      <MatrixModal
        show={matrixModal.show}
        role={matrixModal.role}
        activeModules={activeModules}
        onClose={() => setMatrixModal({ show: false, role: null })}
        onSaved={handleSaved}
      />
    </div>
  );
}
